/**
 * Parser markdown minimaliste, pur et déterministe, produisant un AST de blocs.
 *
 * Couvre le sous-ensemble utile aux descriptions de scénario : titres (`#`…),
 * paragraphes, listes à puces / numérotées, citations, règles horizontales, et
 * en inline **gras**, *italique*, `code`, [liens](url). Volontairement sans
 * dépendance ni HTML brut : le rendu (voir `Markdown.tsx`) produit des éléments
 * React, ce qui échappe le texte par construction — indispensable car la
 * description peut provenir d'une source non fiable (lien de partage `#s=…`).
 */

export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'code'; value: string }
  | { type: 'link'; href: string; children: InlineNode[] };

export type Block =
  | { type: 'heading'; level: number; children: InlineNode[] }
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'ul'; items: InlineNode[][] }
  | { type: 'ol'; items: InlineNode[][] }
  | { type: 'blockquote'; children: InlineNode[] }
  | { type: 'hr' };

// Schémas d'URL autorisés pour les liens ; tout le reste (ex. `javascript:`)
// est neutralisé en rendant le texte du lien sans ancre.
const SAFE_URL = /^(https?:|mailto:|tel:|#|\/|\.\/|\.\.\/)/i;

export function isSafeHref(href: string): boolean {
  return SAFE_URL.test(href.trim());
}

interface InlineRule {
  regex: RegExp;
  make: (m: RegExpExecArray) => InlineNode;
}

// Ordre = précédence : le code d'abord (contenu littéral), puis liens, gras,
// italique. Chaque motif est ancré pour être trouvé par `indexOf` via `exec`.
const INLINE_RULES: InlineRule[] = [
  {
    regex: /`([^`]+)`/,
    make: (m) => ({ type: 'code', value: m[1] ?? '' }),
  },
  {
    regex: /\[([^\]]+)\]\(([^)\s]+)\)/,
    make: (m) => {
      const href = m[2] ?? '';
      const text = m[1] ?? '';
      if (!isSafeHref(href)) {
        // Lien non fiable : on conserve le texte, sans ancre.
        return { type: 'text', value: text };
      }
      return { type: 'link', href, children: parseInline(text) };
    },
  },
  {
    regex: /\*\*([^*]+)\*\*|__([^_]+)__/,
    make: (m) => ({ type: 'strong', children: parseInline(m[1] ?? m[2] ?? '') }),
  },
  {
    regex: /\*([^*]+)\*|_([^_]+)_/,
    make: (m) => ({ type: 'em', children: parseInline(m[1] ?? m[2] ?? '') }),
  },
];

export function parseInline(text: string): InlineNode[] {
  if (text === '') return [];

  let earliest: { index: number; length: number; node: InlineNode } | null = null;
  for (const rule of INLINE_RULES) {
    const m = rule.regex.exec(text);
    if (m && (earliest === null || m.index < earliest.index)) {
      earliest = { index: m.index, length: m[0].length, node: rule.make(m) };
    }
  }

  if (earliest === null) {
    return [{ type: 'text', value: text }];
  }

  const nodes: InlineNode[] = [];
  if (earliest.index > 0) {
    nodes.push({ type: 'text', value: text.slice(0, earliest.index) });
  }
  nodes.push(earliest.node);
  nodes.push(...parseInline(text.slice(earliest.index + earliest.length)));
  return nodes;
}

const HEADING = /^(#{1,6})\s+(.*)$/;
const HR = /^(?:-{3,}|\*{3,}|_{3,})$/;
const UL_ITEM = /^\s*[-*+]\s+(.*)$/;
const OL_ITEM = /^\s*\d+\.\s+(.*)$/;
const BLOCKQUOTE = /^\s*>\s?(.*)$/;

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];

  let paragraph: string[] = [];
  let ulItems: string[] | null = null;
  let olItems: string[] | null = null;
  let quoteLines: string[] | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', children: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
  };
  const flushUl = () => {
    if (ulItems) {
      blocks.push({ type: 'ul', items: ulItems.map((i) => parseInline(i)) });
      ulItems = null;
    }
  };
  const flushOl = () => {
    if (olItems) {
      blocks.push({ type: 'ol', items: olItems.map((i) => parseInline(i)) });
      olItems = null;
    }
  };
  const flushQuote = () => {
    if (quoteLines) {
      blocks.push({ type: 'blockquote', children: parseInline(quoteLines.join(' ')) });
      quoteLines = null;
    }
  };
  const flushAll = () => {
    flushParagraph();
    flushUl();
    flushOl();
    flushQuote();
  };

  for (const line of lines) {
    if (line.trim() === '') {
      flushAll();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushAll();
      blocks.push({
        type: 'heading',
        level: (heading[1] ?? '#').length,
        children: parseInline((heading[2] ?? '').trim()),
      });
      continue;
    }

    if (HR.test(line.trim())) {
      flushAll();
      blocks.push({ type: 'hr' });
      continue;
    }

    const quote = BLOCKQUOTE.exec(line);
    if (quote) {
      flushParagraph();
      flushUl();
      flushOl();
      quoteLines ??= [];
      quoteLines.push(quote[1] ?? '');
      continue;
    }

    const ul = UL_ITEM.exec(line);
    if (ul) {
      flushParagraph();
      flushOl();
      flushQuote();
      ulItems ??= [];
      ulItems.push(ul[1] ?? '');
      continue;
    }

    const ol = OL_ITEM.exec(line);
    if (ol) {
      flushParagraph();
      flushUl();
      flushQuote();
      olItems ??= [];
      olItems.push(ol[1] ?? '');
      continue;
    }

    // Ligne de texte ordinaire : accumulée dans le paragraphe courant.
    flushUl();
    flushOl();
    flushQuote();
    paragraph.push(line.trim());
  }

  flushAll();
  return blocks;
}
